import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    RequeueResult as RequeueResultData,
    SyncStatusSummary,
    statusKey,
} from '../../api/requeue';
import './Requeue.scss';

export interface IRequeueResultProps {
    result: RequeueResultData | null;
    statuses: Record<string, SyncStatusSummary>;
    error: string | null;

    onReloadList: () => void;
    onDismiss: () => void;
}

/**
 * How many result rows we draw. A table with thousands of rows locks the browser up just
 * like the list itself does; the counts above tell the whole story, and the "In Exact"
 * column in the list shows the rest.
 */
const MAX_RESULT_ROWS = 100;

/**
 * What came of a requeue.
 *
 * Only that: this card exists while there is something to report about the batch that was
 * just sent, and closes when you dismiss it. What happens to those objects afterwards is
 * the tracking list's job, and that one lives in its own section above - see
 * TrackingPanel.
 */
export const RequeueResult = (props: IRequeueResultProps) => {
    if (!props.result && !props.error) {
        return null;
    }

    /*
        A refusal is not a failure and does not get counted as one. The API refuses an
        object whose type the target queue's consumer has no handler for, because sending it
        would delete the message and record the object as synchronised - so "geweigerd" is
        the API doing its job, while "mislukt" is something that went wrong.
    */
    const refused = props.result?.refused ?? 0;
    const blocked = props.result?.blocked ?? 0;

    const failed = props.result
        ? props.result.results.filter((r) => !!r.error).length -
          refused -
          blocked
        : 0;

    const shown = props.result
        ? props.result.results.slice(0, MAX_RESULT_ROWS)
        : [];

    const hidden = props.result ? props.result.results.length - shown.length : 0;

    return (
        <div className="requeue-result">
            {props.error && (
                <p className="requeue-result__error" role="alert">
                    <FontAwesomeIcon icon="triangle-exclamation" className="me-2" />
                    {props.error}
                </p>
            )}

            {props.result && (
                <>
                    <div className="requeue-result__head">
                        <strong>
                            {props.result.queued} van {props.result.unique}{' '}
                            object(en) op "{props.result.queueName}" gezet
                        </strong>

                        {props.result.requested !== props.result.unique && (
                            <span className="requeue-result__aside">
                                {props.result.requested - props.result.unique}{' '}
                                dubbel overgeslagen
                            </span>
                        )}

                        {refused > 0 && (
                            <span
                                className="requeue-result__failed"
                                title={`De function achter "${props.result.queueName}" verwerkt deze objectsoorten niet. Was het bericht toch verstuurd, dan verdween het van de queue zonder foutregel en kwam het object als geslaagd in de synclog te staan. Per regel staat hieronder op welke queue het wel hoort.`}
                            >
                                {refused} geweigerd: verkeerde queue
                            </span>
                        )}

                        {blocked > 0 && (
                            <span
                                className="requeue-result__failed"
                                title="Niet verstuurd, omdat opnieuw aanbieden hier niets kan oplossen, iets dubbel zou boeken of het object dubbel op de queue zou zetten: bijvoorbeeld opgeruimde gegevens, een stornering, een factuur die in Exact niet meer openstaat, of een object dat nog op de queue wacht. Per regel staat hieronder waarom."
                            >
                                {blocked} tegengehouden
                            </span>
                        )}

                        {failed > 0 && (
                            <span className="requeue-result__failed">
                                {failed} mislukt
                            </span>
                        )}

                        <button
                            type="button"
                            className="requeue-link ms-auto"
                            onClick={props.onReloadList}
                        >
                            Lijst opnieuw ophalen
                        </button>

                        <button
                            type="button"
                            className="requeue-result__close"
                            onClick={props.onDismiss}
                            title="Sluiten"
                        >
                            <FontAwesomeIcon icon="xmark" />
                        </button>
                    </div>

                    <div className="requeue-result__table-wrap">
                        <table className="requeue-result__table">
                            <thead>
                                <tr>
                                    <th>Id</th>
                                    <th>Bericht</th>
                                    <th>In Exact</th>
                                </tr>
                            </thead>
                            <tbody>
                                {shown.map((r) => {
                                    const status =
                                        props.statuses[
                                            statusKey(r.objectType, r.relationId)
                                        ];

                                    return (
                                        <tr key={r.message}>
                                            <td>{r.relationId}</td>
                                            <td>
                                                <code>{r.message}</code>
                                            </td>
                                            <td>
                                                {r.error ? (
                                                    <span className="requeue-result__failed">
                                                        {r.error}
                                                    </span>
                                                ) : status ? (
                                                    <span
                                                        className={
                                                            status.syncedToExact
                                                                ? 'requeue-result__ok'
                                                                : 'requeue-result__pending'
                                                        }
                                                    >
                                                        {status.summary}
                                                    </span>
                                                ) : (
                                                    <span className="requeue-result__pending">
                                                        staat in de wachtrij
                                                    </span>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    {hidden > 0 && (
                        <p className="requeue-result__note">
                            Nog {hidden} object(en) niet in deze tabel getoond. Die
                            worden wel gevolgd; de kolom "In Exact" in de lijst
                            hieronder laat ze zien.
                        </p>
                    )}
                </>
            )}
        </div>
    );
};
